import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  type AcceptedFileType,
} from '@/utils/acceptedFileAttributes';

export type DragState = 'idle' | 'supported' | 'unsupported';

const toArray = (
  fileType: AcceptedFileType | AcceptedFileType[]
): AcceptedFileType[] => {
  return typeof fileType === 'string' ? [fileType] : fileType;
};

const acceptedMimeTypes = (
  fileType: AcceptedFileType | AcceptedFileType[]
): Set<string> => {
  return new Set(
    toArray(fileType).flatMap((type) => ACCEPTED_MIME_TYPES[type])
  );
};

const acceptedExtensions = (
  fileType: AcceptedFileType | AcceptedFileType[]
): readonly string[] => {
  return toArray(fileType).flatMap((type) => ACCEPTED_EXTENSIONS[type]);
};

export const dragContainsFiles = (
  dataTransfer: DataTransfer | null
): boolean => {
  return dataTransfer
    ? Array.from(dataTransfer.types).includes('Files')
    : false;
};

// Browsers expose MIME on `items` during dragenter/over (but not file names).
// Empty MIME (some OS/browsers don't surface it during drag) is treated as
// optimistically supported, then re-validated against name + MIME on drop.
export const inferDragState = (
  dataTransfer: DataTransfer | null,
  fileType: AcceptedFileType | AcceptedFileType[]
): DragState => {
  if (!dragContainsFiles(dataTransfer)) {
    return 'idle';
  }
  const accepted = acceptedMimeTypes(fileType);
  const items = Array.from(dataTransfer?.items ?? []).filter(
    (item) => item.kind === 'file'
  );
  if (items.length === 0) {
    return 'supported';
  }
  const allSupported = items.every(
    (item) => item.type === '' || accepted.has(item.type)
  );
  return allSupported ? 'supported' : 'unsupported';
};

export const isSupportedFile = (
  file: File,
  fileType: AcceptedFileType | AcceptedFileType[]
): boolean => {
  if (acceptedMimeTypes(fileType).has(file.type)) {
    return true;
  }
  const lowerName = file.name.toLowerCase();
  return acceptedExtensions(fileType).some((ext) => lowerName.endsWith(ext));
};
